import { createSelector } from '@ngrx/store';
import { format } from 'date-fns';
import { AppSelectors } from 'src/app/state/app/selectors';
import { PlantsSelectors } from '../plants/selectors';
import { State } from '../reducers';
import { groupBy, orderBy} from 'lodash-es';
import { createQuietNetworkState } from 'src/app/modules/utils/helpers/network-entities';

export const reportStateSelector = (state: State) => state.report;

const TOPPLANTS_COUNT = 4;

export class ResultSelectors {

  // Pull in plants form the catalogue, allows lookups of the plant by id in the reports.
  static plantLookUp = createSelector(PlantsSelectors.extendedPlants, (plants) =>
    plants.reduce((prev, cur) => ({ ...prev, [cur.id]: cur }), {})
  );

  static _getRecentBloomingData = createSelector(
    reportStateSelector,
    ResultSelectors.plantLookUp,
    (state, lookup) =>
      state.recentBloomingData.map((report) => ({
        ...report,
        reportedPlant: lookup[report.reportedPlant.id] || report.reportedPlant,
      }))
  );

  static getLivedataByKey = createSelector(
    reportStateSelector,
    (state) => state.livedataByKey || {}
  );

  static getLivedataLoadingByKey = createSelector(
    reportStateSelector,
    (state) => state.livedataLoadingByKey || {}
  );

  // Filtering state for the report view
  static _getBloomingDataFilter = createSelector(
    reportStateSelector,
    (state) => state.bloomingDataFilter
  );

  // Filtering state for the report view
  // It takes the user's current region (from GPS or manual selection), if a location in the filter is not set
  static getBloomingDataFilter = createSelector(
    ResultSelectors._getBloomingDataFilter,
    AppSelectors.userRegion,
    (bloomingDataFilter, userRegion) => ({
      ...bloomingDataFilter,
      locationId: bloomingDataFilter.locationId || userRegion?.locationId,
    })
  );

  static getLivedataQuery = createSelector(
    ResultSelectors.getBloomingDataFilter,
    (filter) => ({
      year: filter.year,
    })
  );

  static getLivedataKey = createSelector(
    ResultSelectors.getLivedataQuery,
    ({ year }) => year ? `${year}` : null
  );

  static getLivedataCacheEntry = createSelector(
    ResultSelectors.getLivedataByKey,
    ResultSelectors.getLivedataKey,
    (cache, key) => (key ? cache[key] : null)
  );

  static getLivedataLoading = createSelector(
    ResultSelectors.getLivedataLoadingByKey,
    ResultSelectors.getLivedataKey,
    (loadingByKey, key) => (key ? loadingByKey[key] || createQuietNetworkState() : createQuietNetworkState())
  );

  static _getLivedata = createSelector(
    ResultSelectors.getLivedataCacheEntry,
    ResultSelectors.plantLookUp,
    (items, lookup) =>
      (items || []).map((report) => ({
        ...report,
        reportedPlant: lookup[report.reportedPlant.id] || report.reportedPlant,
      }))
  );

  static getCurrentLivedata = createSelector(
    ResultSelectors._getLivedata,
    (items) => items
  );

  static getLivedataForVisLoc = createSelector(
    ResultSelectors.getCurrentLivedata,
    ResultSelectors.getBloomingDataFilter,
    (livedata, filter) =>
      livedata.filter((report) => (report.locations_list || []).indexOf(filter.locationId) !== -1)
  );

  static getFilteredLivedata = createSelector(
    ResultSelectors.getLivedataForVisLoc,
    ResultSelectors.getBloomingDataFilter,
    (bloomingData, filter) => {
      let filteredData = bloomingData;

      filteredData = filter.plants.length > 0
        ? filteredData.filter((item) => filter.plants.includes(item.reportedPlant.id))
        : filteredData;

      if (filter.dateRange?.fromDate || filter.dateRange?.toDate) {
        const fromTs = filter.dateRange?.fromDate?.getTime();
        const toTs = filter.dateRange?.toDate?.getTime();

        filteredData = filteredData.filter((item) => {
          const reportTs = Date.parse(item.reportDate);
          if (isNaN(reportTs)) {
            return false;
          }
          return (!fromTs || reportTs > fromTs) && (!toTs || reportTs < toTs);
        });
      }
      return {
        filter,
        filteredData,
      };
    }
  );

  static livedataReportsGroupedByPlant = createSelector(
    ResultSelectors.getLivedataForVisLoc, (data) => {
      const groupDict = groupBy(data, 'reportedPlant.id');
      const mapped = Object.keys(groupDict).map((key) => {
        const items = groupDict[key];
        const dates = items.map((obsv) => obsv.reportDate).sort();
        return {
          items,
          plant: items[0].reportedPlant,
          from: dates[0],
          to: dates[dates.length - 1],
        };
      });
      return orderBy(mapped, (group) => group.plant.name);
    }
  );

  static livedataMaxByWeek = createSelector(
    ResultSelectors.getLivedataForVisLoc, (data) => {
      const reportDates = data.map(report => {
        const ts = Date.parse(report.reportDate);
        return isNaN(ts) ? null : new Date(ts);
      }).filter(date => !!date);
      const groupDict = groupBy(reportDates, (dt) => {
        return format(dt, 'yyyy-w');
      });
      const counts = Object.keys(groupDict).map((key) => groupDict[key].length);
      return counts.length > 0 ? Math.max(...counts) : 0;
    }
  );

  static getRecentBloomingDataForVisLoc = createSelector(
    ResultSelectors._getRecentBloomingData,
    ResultSelectors.getBloomingDataFilter,
    (bloomingData, bloomingFilter) => {
      const byLocation = bloomingData.filter(
        (report) => (report.locations_list || []).indexOf(bloomingFilter.locationId) !== -1
      );
      if (!bloomingFilter.year) {
        return byLocation;
      }
      return byLocation.filter(
        (report) =>
          report.reportDate && new Date(report.reportDate).getFullYear() === bloomingFilter.year
      );
    }
  );
  // User Stats
  static getTopListCacheFlag = createSelector(
    reportStateSelector,
    (state) => state.toplistDataCacheFlag
  );
  static getTopListLoading = createSelector(
    reportStateSelector,
    (state) => state.toplistDataLoading
  );
  // Top List data + Variants
  static getTopList = createSelector(reportStateSelector, (state) => state.toplistData || []);
  static getTopListAll = createSelector(ResultSelectors.getTopList, (toplist) =>
    toplist.filter((tl) => tl.range === 'all')
  );
  static getTopListYTD = createSelector(ResultSelectors.getTopList, (toplist) =>
    toplist.filter((tl) => tl.range === 'ytd')
  );

}
